import React from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { displayToastError, displayToastSuccess } from '@/helpers';
import { deleteOrganizationLogo, getLogoUploadUrl, getOrganizationLogoUrl } from '@/modules/common';
import { FileUploadField } from '@/modules/common/components/organisms/file-upload-field';

interface OrganizationLogoFieldProps {
  className?: string;
  /** Called when file validation error state changes (true = has error, false = no error) */
  onValidationErrorChange?: (hasError: boolean) => void;
}

export const OrganizationLogoField: React.FC<OrganizationLogoFieldProps> = ({
  className,
  onValidationErrorChange,
}) => {
  const { t } = useTranslation(['authenticated.organization']);
  const { control } = useForm<{ logo: File | null }>({
    defaultValues: { logo: null },
  });

  const config = {
    queryKey: ['organizationLogoUrl'],
    getFileUrl: getOrganizationLogoUrl,
    getUploadUrl: ({ filename, fileSize }: { filename: string; fileSize: number }) =>
      getLogoUploadUrl(filename, fileSize),
    deleteFile: deleteOrganizationLogo,
    allowedTypes: ['image/jpeg', 'image/png', 'image/jpg'],
    maxSize: 2 * 1024 * 1024, // 2MB
    accept: 'image/jpeg,image/png,image/jpg',
    onUploadSuccess: () => displayToastSuccess(t('logo.uploadSuccess')),
    onUploadError: () => displayToastError(t('logo.uploadFailed')),
    onDeleteSuccess: () => displayToastSuccess(t('logo.deleteSuccess')),
    onDeleteError: () => displayToastError(t('logo.deleteFailed')),
  };

  const texts = {
    loading: t('logo.loading'),
    error: t('logo.error'),
    noFileChosen: t('logo.noFileChosen'),
    uploading: t('logo.uploading'),
    chooseFile: t('logo.chooseFile'),
    deleteFile: t('logo.deleteFile'),
    invalidType: t('logo.invalidType'),
    invalidSize: t('logo.invalidSize'),
  };

  return (
    <FileUploadField
      name="logo"
      control={control}
      label={t('logo.logo')}
      className={className}
      helpText={t('logo.fileRequirements')}
      config={config}
      texts={texts}
      onValidationErrorChange={onValidationErrorChange}
    />
  );
};
